export interface PdfToolMeta {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  category: "Organize & Edit" | "Optimize & Repair" | "Convert to PDF" | "Convert from PDF" | "Security & Utilities";
  icon: string;
  featured?: boolean;
  keywords: string[];
}

export const PDF_CATEGORIES = [
  "Organize & Edit",
  "Optimize & Repair",
  "Convert to PDF",
  "Convert from PDF",
  "Security & Utilities",
] as const;

export type PdfCategory = (typeof PDF_CATEGORIES)[number];

export const PDF_TOOLS: PdfToolMeta[] = [
  // Organize & Edit
  {
    slug: "merge-pdf",
    name: "Merge PDF",
    tagline: "Combine several PDFs into one clean document",
    description: "Join two or more PDF files into a single organized document. Choose page ranges, reorder files, and download in seconds.",
    category: "Organize & Edit",
    icon: "FilePlus",
    featured: true,
    keywords: ["merge pdf", "combine pdf", "join pdf", "pdf merger"],
  },
  {
    slug: "split-pdf",
    name: "Split PDF",
    tagline: "Extract pages or split into separate files",
    description: "Separate pages or extract custom page ranges into individual PDF documents easily.",
    category: "Organize & Edit",
    icon: "Scissors",
    featured: true,
    keywords: ["split pdf", "extract pdf pages", "separate pdf"],
  },
  {
    slug: "organize-pdf",
    name: "Organize PDF",
    tagline: "Reorder, delete, and rotate pages visually",
    description: "Visually drag and drop PDF pages to reorder, delete unwanted pages, or rotate individual pages.",
    category: "Organize & Edit",
    icon: "LayoutGrid",
    featured: true,
    keywords: ["organize pdf", "reorder pdf pages", "delete pdf pages"],
  },
  {
    slug: "rotate-pdf",
    name: "Rotate PDF",
    tagline: "Rotate all or selected pages permanently",
    description: "Fix upside down or sideways PDF pages by rotating specific pages or the entire document 90, 180, or 270 degrees.",
    category: "Organize & Edit",
    icon: "RotateCw",
    keywords: ["rotate pdf", "turn pdf", "change pdf orientation"],
  },
  {
    slug: "extract-pages",
    name: "Extract Pages",
    tagline: "Pull selected pages into a new PDF document",
    description: "Select specific pages from a large PDF file and export them into a separate, clean PDF.",
    category: "Organize & Edit",
    icon: "FileOutput",
    keywords: ["extract pdf pages", "save specific pdf pages"],
  },
  {
    slug: "remove-pages",
    name: "Remove Pages",
    tagline: "Delete unwanted pages from your PDF file",
    description: "Select and permanently remove blank or unwanted pages from any PDF document.",
    category: "Organize & Edit",
    icon: "Trash2",
    keywords: ["remove pdf pages", "delete pdf page"],
  },
  {
    slug: "edit-pdf",
    name: "Edit PDF",
    tagline: "Add text, shapes, highlights, images & drawings",
    description: "Add annotations, text boxes, freehand drawings, signatures, and images directly onto your PDF pages.",
    category: "Organize & Edit",
    icon: "Edit3",
    featured: true,
    keywords: ["edit pdf", "add text to pdf", "annotate pdf"],
  },
  {
    slug: "page-numbers",
    name: "Page Numbers",
    tagline: "Add custom page numbers to header or footer",
    description: "Number your PDF pages with customizable positioning, font size, formatting, and margin settings.",
    category: "Organize & Edit",
    icon: "Hash",
    keywords: ["add page numbers to pdf", "number pdf pages"],
  },
  {
    slug: "watermark-pdf",
    name: "Watermark PDF",
    tagline: "Stamp custom text or image watermarks on pages",
    description: "Protect your intellectual property by stamping text or logo watermarks across your PDF pages.",
    category: "Organize & Edit",
    icon: "Droplets",
    keywords: ["watermark pdf", "add text watermark to pdf"],
  },

  // Optimize & Repair
  {
    slug: "compress-pdf",
    name: "Compress PDF",
    tagline: "Reduce PDF file size without losing quality",
    description: "Compress heavy PDF files for faster email sending and uploading while maintaining document clarity.",
    category: "Optimize & Repair",
    icon: "Archive",
    featured: true,
    keywords: ["compress pdf", "reduce pdf size", "shrink pdf"],
  },
  {
    slug: "grayscale-pdf",
    name: "Grayscale PDF",
    tagline: "Convert PDF pages to black & white",
    description: "Convert color PDF documents to grayscale black and white to save printer ink and toner.",
    category: "Optimize & Repair",
    icon: "SunMedium",
    keywords: ["grayscale pdf", "black and white pdf"],
  },
  {
    slug: "repair-pdf",
    name: "Repair PDF",
    tagline: "Fix damaged or unreadable PDF files",
    description: "Recover data and repair corrupted or broken PDF documents directly in your browser.",
    category: "Optimize & Repair",
    icon: "Wrench",
    keywords: ["repair pdf", "fix corrupted pdf", "recover pdf"],
  },

  // Convert to PDF
  {
    slug: "image-to-pdf",
    name: "Images to PDF",
    tagline: "Convert JPG, PNG, WEBP & GIF to PDF",
    description: "Combine multiple photos and image files into a single formatted PDF document.",
    category: "Convert to PDF",
    icon: "Image",
    featured: true,
    keywords: ["image to pdf", "jpg to pdf", "png to pdf"],
  },
  {
    slug: "word-to-pdf",
    name: "Word to PDF",
    tagline: "Convert DOCX & DOC documents to PDF",
    description: "Convert Word documents to clean, professional PDF files directly in your browser.",
    category: "Convert to PDF",
    icon: "FileText",
    keywords: ["word to pdf", "docx to pdf"],
  },
  {
    slug: "excel-to-pdf",
    name: "Excel to PDF",
    tagline: "Convert XLSX spreadsheets to PDF tables",
    description: "Turn Excel sheets into formatted PDF documents ready for printing and presentation.",
    category: "Convert to PDF",
    icon: "Table",
    keywords: ["excel to pdf", "xlsx to pdf"],
  },
  {
    slug: "ppt-to-pdf",
    name: "PowerPoint to PDF",
    tagline: "Convert PPTX slides into a PDF document",
    description: "Convert presentation slides into compact PDF documents.",
    category: "Convert to PDF",
    icon: "Presentation",
    keywords: ["ppt to pdf", "powerpoint to pdf"],
  },
  {
    slug: "html-to-pdf",
    name: "HTML to PDF",
    tagline: "Save web pages or HTML code as PDF",
    description: "Convert raw HTML code or web page content into a clean PDF document.",
    category: "Convert to PDF",
    icon: "Code",
    keywords: ["html to pdf", "webpage to pdf"],
  },
  {
    slug: "txt-to-pdf",
    name: "Text to PDF",
    tagline: "Convert plain text files to PDF documents",
    description: "Convert raw .txt files into formatted PDF documents with custom font styling.",
    category: "Convert to PDF",
    icon: "FileType",
    keywords: ["txt to pdf", "text file to pdf"],
  },

  // Convert from PDF
  {
    slug: "pdf-to-image",
    name: "PDF to Images",
    tagline: "Extract PDF pages into JPG or PNG images",
    description: "Convert every page of a PDF file into high-resolution JPG or PNG images.",
    category: "Convert from PDF",
    icon: "FileImage",
    featured: true,
    keywords: ["pdf to image", "pdf to jpg", "pdf to png"],
  },
  {
    slug: "pdf-to-word",
    name: "PDF to Word",
    tagline: "Extract text & convert PDF to editable DOCX",
    description: "Convert PDF documents into editable Word files while preserving text layout.",
    category: "Convert from PDF",
    icon: "FileCode",
    keywords: ["pdf to word", "pdf to docx"],
  },
  {
    slug: "pdf-to-excel",
    name: "PDF to Excel",
    tagline: "Extract tables into XLSX spreadsheets",
    description: "Extract data tables from PDF documents into editable Excel spreadsheets.",
    category: "Convert from PDF",
    icon: "Spreadsheet",
    keywords: ["pdf to excel", "pdf to xlsx"],
  },
  {
    slug: "pdf-to-text",
    name: "PDF to Text",
    tagline: "Extract raw plain text from PDF files",
    description: "Extract all text contents from a PDF file into a lightweight .txt file.",
    category: "Convert from PDF",
    icon: "FileLines",
    keywords: ["pdf to text", "extract text from pdf"],
  },

  // Security & Utilities
  {
    slug: "protect-pdf",
    name: "Protect PDF",
    tagline: "Encrypt and password-protect your PDF",
    description: "Secure confidential PDF files with 128-bit encryption and custom password protection.",
    category: "Security & Utilities",
    icon: "Lock",
    featured: true,
    keywords: ["protect pdf", "password protect pdf", "encrypt pdf"],
  },
  {
    slug: "unlock-pdf",
    name: "Unlock PDF",
    tagline: "Remove password protection from PDF",
    description: "Remove passwords and restriction locks from PDF documents you own.",
    category: "Security & Utilities",
    icon: "Unlock",
    keywords: ["unlock pdf", "remove password from pdf"],
  },
  {
    slug: "sign-pdf",
    name: "Sign PDF",
    tagline: "Add digital signature or draw signature",
    description: "Draw your signature or upload a signature image to sign contracts and forms.",
    category: "Security & Utilities",
    icon: "PenTool",
    featured: true,
    keywords: ["sign pdf", "e-sign pdf", "digital signature pdf"],
  },
  {
    slug: "ocr-pdf",
    name: "OCR PDF",
    tagline: "Recognize text from scanned PDF documents",
    description: "Optical Character Recognition to convert scanned PDF documents into searchable text.",
    category: "Security & Utilities",
    icon: "ScanText",
    keywords: ["ocr pdf", "scanned pdf to text"],
  },
  {
    slug: "qr-code",
    name: "QR Code Generator",
    tagline: "Create custom QR codes for links, WiFi, vCards & text",
    description: "Generate styled QR codes for websites, WiFi networks, contact cards, and text. Download as PNG or stamp directly onto PDF pages.",
    category: "Security & Utilities",
    icon: "QrCode",
    featured: true,
    keywords: ["qr code generator", "create qr code", "qr code for url", "wifi qr code", "vcard qr code"],
  },
];

export function getPdfToolBySlug(slug: string): PdfToolMeta | undefined {
  return PDF_TOOLS.find((t) => t.slug === slug);
}

export function relatedPdfTools(currentSlug: string, count = 4): PdfToolMeta[] {
  return PDF_TOOLS.filter((t) => t.slug !== currentSlug).slice(0, count);
}
